import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  :root {
    --primary-color: #3b82f6;
    --primary-light: #60a5fa;
    --primary-dark: #2563eb;
    --secondary-color: #334155;
    --background-dark: rgba(10, 10, 10, 0.7);
    --background-light: rgba(20, 20, 20, 0.75);
    --background-card: rgba(30, 30, 30, 0.8);
    --text-light: #f8fafc;
    --text-dim: #94a3b8;
    --card-bg: rgba(26, 26, 26, 0.75);
    --glass-bg: rgba(30, 30, 30, 0.3);
    --glass-border: rgba(255, 255, 255, 0.12);
    --sidebar-width: 240px;
    --header-height: 70px;
    --border-radius: 10px;
    --transition-speed: 0.3s;
    --card-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.25);
    --success-color: #10b981;
    --warning-color: #f59e0b;
    --error-color: #ef4444;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    background: linear-gradient(135deg, #0a0a0a, #151515);
    color: var(--text-light);
    line-height: 1.6;
    overflow-x: hidden;
    min-height: 100vh;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  button {
    cursor: pointer;
    font-family: inherit;
  }

  ul {
    list-style: none;
  }

  /* Card container styles */
  .card-container {
    background-color: var(--card-bg);
    border-radius: var(--border-radius);
    border: 1px solid var(--glass-border);
    box-shadow: var(--card-shadow);
    backdrop-filter: blur(10px);
    overflow: hidden;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }
  
  .card-container:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px 0 rgba(0, 0, 0, 0.3);
  }

  /* Scrollbar styles */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }

  /* Utility classes */
  .btn {
    padding: 10px 20px;
    border: none;
    border-radius: var(--border-radius);
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all var(--transition-speed);
    backdrop-filter: blur(4px);
  }

  .btn-primary {
    background-color: rgba(59, 130, 246, 0.8);
    color: white;
    box-shadow: 0 4px 10px rgba(59, 130, 246, 0.25);
  }

  .btn-primary:hover {
    background-color: var(--primary-light);
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 6px 18px rgba(59, 130, 246, 0.4);
  }

  .btn-secondary {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--text-light);
    border: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  }

  .btn-secondary:hover {
    background-color: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2);
  }

  .btn svg {
    margin-right: 10px;
  }
  
  .loading-spinner {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100px;
  }
  
  .loading-spinner::after {
    content: "";
    width: 40px;
    height: 40px;
    border: 3px solid rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    border-top-color: var(--primary-light);
    animation: spin 1s ease-in-out infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  .error-message {
    color: #ef4444;
    text-align: center;
    padding: 20px;
    background-color: rgba(239, 68, 68, 0.1);
    border-radius: var(--border-radius);
    margin: 20px 0;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(239, 68, 68, 0.2);
    box-shadow: 0 5px 15px rgba(239, 68, 68, 0.1);
  }

  /* Glass morphism utility class */
  .glass {
    background: var(--glass-bg);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid var(--glass-border);
    box-shadow: var(--card-shadow);
    transition: all 0.3s ease;
  }
  
  .glass:hover {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.15);
  }
  
  /* Input styles */
  input, select, textarea {
    background-color: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: var(--text-light);
    border-radius: 8px;
    padding: 12px 15px;
    transition: all 0.2s ease;
  }
  
  input:focus, select:focus, textarea:focus {
    outline: none;
    border-color: var(--primary-light);
    background-color: rgba(255, 255, 255, 0.12);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
  }
`;

export default GlobalStyles;